import { useEffect, useState, ReactElement } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";

export type FavouriteIconKey =
  | "food"
  | "sport"
  | "books"
  | "quote"
  | "coffee"
  | "heart"
  | "drink";

export interface FavouriteItem {
  id: number;
  name: string;
  description: string;
  icon_name?: FavouriteIconKey;
  icon_bg?: string;
  deceased?: number;
}

const ICON_OPTIONS: { value: FavouriteIconKey; label: string; icon: ReactElement }[] = [
  { value: "quote", label: "Quote", icon: <FormatQuoteIcon /> },
  { value: "heart", label: "Heart", icon: <FavoriteIcon /> },
  { value: "coffee", label: "Coffee", icon: <LocalCafeIcon /> },
  { value: "books", label: "Book", icon: <MenuBookIcon /> },
  { value: "food", label: "Food", icon: <RestaurantIcon /> },
  { value: "sport", label: "Sport", icon: <SportsSoccerIcon /> },
  { value: "drink", label: "Drink", icon: <LocalBarIcon /> },
];

const getIcon = (iconName?: string) => {
  const found = ICON_OPTIONS.find((o) => o.value === iconName?.toLowerCase());
  return found ? found.icon : <FormatQuoteIcon />;
};

interface FavouritesTabProps {
  deceasedId?: number | null;
  deceasedFirstName?: string;
  accentColor?: string;
  isAdmin?: boolean;
  /** "standalone" (default) wraps content in the white motion/Card chrome
   * used across template-1's other tabs. "embedded" renders just the
   * content, matching template-2's own floating tab design (e.g. Memories). */
  variant?: "standalone" | "embedded";
}

export default function FavouritesTab({
  deceasedId,
  deceasedFirstName,
  accentColor,
  isAdmin = false,
  variant = "standalone",
}: FavouritesTabProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [favourites, setFavourites] = useState<FavouriteItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FavouriteItem | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIcon, setFormIcon] = useState<FavouriteIconKey>("quote");
  const [saving, setSaving] = useState(false);

  const brandColor = accentColor || reduxTheme.primary_colors?.[400] || "#6B4A3A";

  useEffect(() => {
    if (!deceasedId) {
      setLoading(false);
      return;
    }

    const fetchFavourites = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<
          FavouriteItem[] | { results: FavouriteItem[] } | { favorites: FavouriteItem[] }
        >(`api/client-portal/favourites/?deceased_id=${deceasedId}`);
        const data = response.data;
        const list = Array.isArray(data)
          ? data
          : (data as { results?: FavouriteItem[] }).results ??
            (data as { favorites?: FavouriteItem[] }).favorites ??
            [];
        setFavourites(list);
      } catch (err) {
        console.error("Failed to fetch favourites", err);
        setFavourites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavourites();
  }, [deceasedId]);

  const openAddModal = () => {
    setEditingItem(null);
    setFormName("");
    setFormDescription("");
    setFormIcon("quote");
    setModalOpen(true);
  };

  const openEditModal = (fav: FavouriteItem) => {
    setEditingItem(fav);
    setFormName(fav.name);
    setFormDescription(fav.description);
    setFormIcon((fav.icon_name as FavouriteIconKey) || "quote");
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!formName.trim() || !deceasedId) return;
    setSaving(true);
    try {
      const payload = {
        name: formName,
        description: formDescription,
        icon_name: formIcon,
        deceased: deceasedId,
      };

      if (editingItem) {
        const res = await apiService.patch<FavouriteItem>(
          `api/client-portal/favourites/${editingItem.id}/`,
          payload
        );
        setFavourites((prev) =>
          prev.map((f) => (f.id === editingItem.id ? res.data : f))
        );
        showSuccessToast("Favourite updated successfully.");
      } else {
        const res = await apiService.post<FavouriteItem>(
          `api/client-portal/favourites/`,
          payload
        );
        setFavourites((prev) => [res.data, ...prev]);
        showSuccessToast("Favourite added successfully.");
      }
      handleClose();
    } catch (err) {
      showErrorToast("Failed to save favourite.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const spinner = (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress style={{ color: brandColor }} />
      </Box>
    );
    return variant === "standalone" ? (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card sx={{ height: "100%", boxShadow: 0, borderRadius: "20px", padding: "1.2rem" }}>
          {spinner}
        </Card>
      </motion.div>
    ) : (
      spinner
    );
  }

  const content = (
    <>
      {/* Admin: Add button header */}
      {isAdmin && (
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button
            variant="contained"
            onClick={openAddModal}
            sx={{
              backgroundColor: brandColor,
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { backgroundColor: brandColor, opacity: 0.9 },
            }}
          >
            Add Favourite
          </Button>
        </Box>
      )}

      {favourites.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={3}>
          <Typography variant="body1" sx={{ color: "text.secondary", fontStyle: "italic", textAlign: "center" }}>
            {isAdmin
              ? `Add favourite moments and memories to share.`
              : `No favourite moments have been added yet.`}
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              onClick={openAddModal}
              sx={{
                backgroundColor: brandColor,
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { opacity: 0.9 },
              }}
            >
              Add Favourite
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {favourites.map((fav) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={fav.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderRadius: 3,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.12)",
                  },
                  backgroundColor: reduxTheme.primary_colors?.[900] || "#F5F5F5",
                }}
              >
                {/* Admin edit button */}
                {isAdmin && (
                  <IconButton
                    size="small"
                    onClick={() => openEditModal(fav)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                <CardContent sx={{ p: 3, display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Avatar
                    sx={{
                      bgcolor: fav.icon_bg || brandColor,
                      color: "#ffffff",
                      width: 44,
                      height: 44,
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    }}
                  >
                    {getIcon(fav.icon_name)}
                  </Avatar>
                  <Box sx={{ flex: 1, pr: isAdmin ? 3 : 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: reduxTheme.primary_colors?.[100] || brandColor, mb: 1 }}
                    >
                      {fav.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {fav.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Write about {deceasedFirstName || "their"}'s Favourites
          <IconButton onClick={handleClose} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tell Us A Bit More About {deceasedFirstName || "their"}'s Favorite Moments & Memories
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Title"
              required
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Jane, Football, Coffee..."
            />
            <TextField
              label="Description"
              required
              fullWidth
              multiline
              rows={4}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Favourite Moments & Memories"
            />

            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                Choose Icon
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {ICON_OPTIONS.map((opt) => {
                  const active = formIcon === opt.value;
                  return (
                    <IconButton
                      key={opt.value}
                      onClick={() => setFormIcon(opt.value)}
                      title={opt.label}
                      sx={{
                        width: 56,
                        height: 56,
                        border: active ? `2px solid ${brandColor}` : "1px solid #CBD5E1",
                        bgcolor: "#fff",
                        color: brandColor,
                      }}
                    >
                      {opt.icon}
                    </IconButton>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formName.trim()}
            sx={{
              textTransform: "none",
              backgroundColor: brandColor,
              "&:hover": { opacity: 0.9 },
            }}
          >
            {saving ? "Saving..." : editingItem ? "Save Changes" : "Add Favourite"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return variant === "standalone" ? (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          height: "100%",
          boxShadow: 0,
          borderRadius: "20px",
          padding: "1.2rem",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {content}
      </Card>
    </motion.div>
  ) : (
    <Box sx={{ width: "100%", py: 2 }}>{content}</Box>
  );
}
